import React, { useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { Profile } from '@uncinc/react-drupal-core';

import splitListItems from '../../../helpers/splitListItems';
import ButtonWrapper from '../../ButtonWrapper';
import type { ProfileDefault, ProfileExternal } from '../../../types/uncinc_profile/profile';

import './style.scss';

const MAX_MEMBERS_SHOWN_BY_DEFAULT = 3;

interface MembersListProps {
  keyPrefix: string;
  initialMembers?: (ProfileDefault | ProfileExternal)[];
  additionalMembers?: (ProfileDefault | ProfileExternal)[];
  hasExternalMembers?: boolean;
  isExpanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const MembersList: React.FC<MembersListProps> = ({
  keyPrefix,
  initialMembers,
  additionalMembers,
  hasExternalMembers = false,
  isExpanded,
  setExpanded,
}) => {
  const { t } = useTranslation('translation', { keyPrefix });
  const ext = hasExternalMembers ? '-external' : '';

  return (
    <div className={`TeamWidget__members${ext}`}>
      <h2 className={`TeamWidget__members${ext}__title asH3`}>
        {hasExternalMembers ? t('members_external_title') : t('members_title')}
      </h2>

      {(initialMembers && initialMembers.length > 0) && (
        <ul className={`TeamWidget__members${ext}__list`}>
          {initialMembers.map((member) => (
            <li key={`team-member-${member.profile_id[0].value}`}>
              <Profile entity={fromJS(member)} viewMode="teaser" />
            </li>
          ))}
        </ul>
      )}

      {(additionalMembers && additionalMembers.length > 0) && (
        <>
          <div className={`TeamWidget__members${ext}__list-wrapper`}>
            <ul className={`TeamWidget__members${ext}__list`}>
              {additionalMembers.map((member) => (
                <li key={`team-member-${member.profile_id[0].value}`}>
                  <Profile entity={fromJS(member)} viewMode="teaser" />
                </li>
              ))}
            </ul>
          </div>

          <ButtonWrapper
            className={`TeamWidget__show-more-members${ext}-button`}
            type="textual"
            iconName="keyboard_arrow_down"
            iconPosition="left"
            onClick={() => setExpanded(!isExpanded)}
          >
            {t(isExpanded ? `members${ext}_show_less_button` : `members${ext}_show_more_button`)}
          </ButtonWrapper>
        </>
      )}
    </div>
  );
};

export interface TeamWidgetProps {
  context: 'article' | 'project';
  leads?: ProfileDefault[];
  secondaryLeads?: ProfileExternal[];
  members: ProfileDefault[];
  externalStaff?: ProfileExternal[];
  className?: string;
}

const TeamWidget: React.FC<TeamWidgetProps> = (props) => {
  const {
    context,
    leads,
    secondaryLeads,
    members,
    externalStaff,
    className,
  } = props;

  const keyPrefix = `components.widgets.team_widget.${context}`;
  const { t } = useTranslation('translation', { keyPrefix });

  const [isExpanded, setExpanded] = useState(false);
  const [isExternalExpanded, setExternalExpanded] = useState(false);

  const [initialMembers, additionalMembers] = useMemo(() => (
    splitListItems<ProfileDefault | ProfileExternal>(
      members,
      MAX_MEMBERS_SHOWN_BY_DEFAULT,
    )
  ), [members]);

  const [initialExternalMembers, additionalExternalMembers] = useMemo(() => (
    splitListItems<ProfileDefault | ProfileExternal>(
      externalStaff || [],
      MAX_MEMBERS_SHOWN_BY_DEFAULT,
    )
  ), [externalStaff]);

  const classes = c('TeamWidget', 'Paper', className, {
    'TeamWidget--expanded': isExpanded,
    'TeamWidget--expanded-external': isExternalExpanded,
  });

  if (!(leads && leads.length > 0) && !(members && members.length > 0)) {
    return null;
  }

  return (
    <div className={classes}>
      {(leads && leads.length > 0) && (
        <div className="TeamWidget__members">
          <h2 className="TeamWidget__members__title asH3">
            {t('leads_title', { count: leads.length })}
          </h2>

          <ul className="TeamWidget__members__list">
            {leads.map((lead) => (
              <li key={`team-lead-${lead.profile_id[0].value}`}>
                <Profile entity={fromJS(lead)} viewMode="teaser" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {(members && members.length > 0) && (
        <MembersList
          keyPrefix={keyPrefix}
          initialMembers={initialMembers}
          additionalMembers={additionalMembers}
          isExpanded={isExpanded}
          setExpanded={setExpanded}
        />
      )}

      {(secondaryLeads && secondaryLeads.length > 0) && (
        <div className="TeamWidget__members">
          <h2 className="TeamWidget__members__title asH3">
            {t('external_leads_title', { count: secondaryLeads.length })}
          </h2>

          <ul className="TeamWidget__members__list">
            {secondaryLeads?.map((lead) => (
              <li key={`team-lead-${lead.profile_id[0].value}`}>
                <Profile entity={fromJS(lead)} viewMode="teaser" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {(externalStaff && externalStaff.length > 0) && (
        <MembersList
          keyPrefix={keyPrefix}
          initialMembers={initialExternalMembers}
          additionalMembers={additionalExternalMembers}
          hasExternalMembers
          isExpanded={isExternalExpanded}
          setExpanded={setExternalExpanded}
        />
      )}
    </div>
  );
};

export default TeamWidget;
